import React from 'react';
import { Shield, Target, Users, Heart, Award, Globe } from 'lucide-react';
import { colors } from '../styles/theme';
import Card3D from '../components/ui/Card3D';
import FadeIn from '../components/animations/FadeIn';
import StatCounter from '../components/ui/StatCounter';
import ValuesSection from '../components/values/ValuesSection';
import PressKitSection from '../components/press/PressKitSection';
import ParallaxHero from '../components/ui/ParallaxHero';
import { Link } from "react-router-dom";


const AboutPage = () => {
  return (
    <FadeIn delay={0.2}>
    <div>
      {/* Hero Section with Parallax */}
      <ParallaxHero 
        imageUrl="https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&q=80"
        overlayColor="rgba(1, 57, 183, 0.5)"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex flex-col justify-center items-center h-full text-center text-white">
          <FadeIn delay={0.2}>
          <span className="inline-block px-4 py-1.5 mb-5 text-sm font-semibold text-blue-700 bg-blue-100/80 backdrop-blur-sm rounded-full">
            FÉDÉRATION FRANCE STUDENT
          </span>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight">
            <span className="text-white inline-block px-3">À</span>
            <span className="relative inline-block px-3 text-white">
            <span className="absolute inset-0 transform -skew-x-12 bg-gradient-to-r from-blue-800 to-blue-500 shadow-lg" />
            <span className="relative">PROPOS</span>
            </span>
            </h1>
      </FadeIn>
      
      <FadeIn delay={0.6}>
        <div className="relative">
          <p className="text-lg md:text-2xl text-white mb-8 mt-8 max-w-4xl mx-auto font-light">
            Découvrez notre association
          </p>
        </div>
      </FadeIn>
          </div>
        </div>
      </ParallaxHero>

      {/* Mission et Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Card3D>
              <div className="bg-white p-8 rounded-2xl shadow-lg h-full">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                  style={{
                    backgroundColor: `${colors.primary}15`,
                    color: colors.primary
                  }}
                >
                  <Target className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold mb-4">Notre Mission</h2>
                <p className="text-gray-600">
                  FRANCE STUDENT s'engage à réduire la fracture numérique dans l'enseignement supérieur 
                  en fournissant aux étudiants les outils, les ressources et le soutien nécessaires 
                  pour réussir dans un monde de plus en plus numérisé.
                </p>
              </div>
            </Card3D>

            <Card3D>
              <div className="bg-white p-8 rounded-2xl shadow-lg h-full">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                  style={{
                    backgroundColor: `${colors.secondary}15`,
                    color: colors.secondary
                  }}
                >
                  <Globe className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold mb-4">Notre Vision</h2>
                <p className="text-gray-600">
                  Nous aspirons à créer un environnement éducatif où chaque étudiant, 
                  indépendamment de sa situation socio-économique, a accès aux outils numériques 
                  et aux compétences nécessaires pour réussir ses études et sa future carrière.
                </p>
              </div>
            </Card3D>
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <ValuesSection />

      {/* Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Notre Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Des chiffres qui témoignent de notre engagement pour l'inclusion numérique
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <Card3D>
              <div className="bg-white p-8 rounded-2xl shadow-lg h-full">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto"
                  style={{
                    backgroundColor: `${colors.primary}15`,
                    color: colors.primary
                  }}
                >
                  <Users className="w-8 h-8" />
                </div>
                <div className="text-4xl mb-2 text-center" style={{ color: colors.primary }}>
                  <StatCounter end={198000} suffix="+" />
                </div>
                <p className="text-gray-600 text-center">Étudiants inscrits</p>
              </div>
            </Card3D>

            <Card3D>
              <div className="bg-white p-8 rounded-2xl shadow-lg h-full">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto"
                  style={{
                    backgroundColor: `${colors.secondary}15`,
                    color: colors.secondary
                  }}
                >
                  <Award className="w-8 h-8" />
                </div>
                <div className="text-4xl mb-2 text-center" style={{ color: colors.secondary }}>
                  <StatCounter end={215000} suffix="+" />
                </div>
                <p className="text-gray-600 text-center">Produits activés</p>
              </div>
            </Card3D>

            <Card3D>
              <div className="bg-white p-8 rounded-2xl shadow-lg h-full">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto"
                  style={{
                    backgroundColor: `${colors.tertiary}15`,
                    color: colors.tertiary
                  }}
                >
                  <Shield className="w-8 h-8" />
                </div>
                <div className="text-4xl mb-2 text-center" style={{ color: colors.tertiary }}>
                  <StatCounter end={22} suffix="+" />
                </div>
                <p className="text-gray-600 text-center">Projets accompagnés</p>
              </div>
            </Card3D>

            <Card3D>
              <div className="bg-white p-8 rounded-2xl shadow-lg h-full">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto"
                  style={{
                    backgroundColor: `${colors.accent1}15`,
                    color: colors.accent1
                  }}
                >
                  <Heart className="w-8 h-8" />
                </div>
                <div className="text-4xl mb-2 text-center" style={{ color: colors.accent1 }}>
                  <StatCounter end={15} suffix="+" />
                </div>
                <p className="text-gray-600 text-center">Bénévoles actifs</p>
              </div>
            </Card3D>
          </div>
        </div>
      </section>

      {/* Dossier de Presse */}
      <PressKitSection />

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-8">Rejoignez l'Aventure</h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            Ensemble, construisons un avenir numérique plus inclusif pour tous les étudiants
          </p>
          <Link to="/jobs">
            <button 
              className="px-8 py-4 bg-[#0139B7] text-white rounded-lg hover:bg-opacity-90 transition-colors"
            >
              Devenir Bénévole
            </button>
          </Link>
        </div>
      </section>
    </div>
    </FadeIn>
  );
};

export default AboutPage;